import {
  EnrichmentFieldState,
  type EnrichmentStatusEntry,
  type RefreshOutcomeDto,
} from '@factoryfixinc/ats-interfaces';
import { type ComputedRef, type Ref, computed, onBeforeUnmount, ref, watch } from 'vue';

interface EnrichmentAnimationOptions {
  outcome: ComputedRef<RefreshOutcomeDto | null>;
  fieldStates: ComputedRef<EnrichmentStatusEntry['fields'] | undefined>;
  phone: ComputedRef<string>;
  email: ComputedRef<string>;
}

interface EnrichmentAnimationState {
  displayedEmail: ComputedRef<string>;
  displayedPhone: ComputedRef<string>;
  handleResolved: () => void;
  isActive: Ref<boolean>;
  isButtonPending: ComputedRef<boolean>;
  isEmailConfirmed: ComputedRef<boolean>;
  isPhoneConfirmed: ComputedRef<boolean>;
}

export function useEnrichmentAnimation({
  outcome,
  fieldStates,
  phone,
  email,
}: EnrichmentAnimationOptions): EnrichmentAnimationState {
  const isActive = ref(false);
  const resolveBeatStarted = ref(false);
  const showFieldConfirmation = ref(false);
  const contactSnapshot = ref<{ phone: string; email: string } | null>(null);
  let fieldRevealTimeoutId: number | null = null;
  let fieldConfirmationTimeoutId: number | null = null;

  watch(
    outcome,
    (nextOutcome) => {
      if (nextOutcome?.outcome === 'checking' && !isActive.value) {
        contactSnapshot.value = { phone: phone.value, email: email.value };
        resolveBeatStarted.value = false;
        isActive.value = true;
      }
    },
    { immediate: true, flush: 'sync' },
  );

  const displayedPhone = computed(() => {
    if (isActive.value) return contactSnapshot.value?.phone ?? '';
    if (fieldStates.value?.phone === EnrichmentFieldState.REMOVED) return '—';
    return phone.value;
  });
  const displayedEmail = computed(() => {
    if (isActive.value) return contactSnapshot.value?.email ?? '';
    if (fieldStates.value?.email === EnrichmentFieldState.REMOVED) return '—';
    return email.value;
  });
  const isButtonPending = computed(() => isActive.value && !resolveBeatStarted.value);
  // Full checking runs show transient field confirmations for every terminal outcome.
  const shouldConfirmFields = computed(
    () => !!outcome.value && outcome.value.outcome !== 'checking',
  );
  const isPhoneConfirmed = computed(
    () =>
      showFieldConfirmation.value &&
      fieldStates.value?.phone !== undefined &&
      fieldStates.value.phone !== EnrichmentFieldState.CHECKING,
  );
  const isEmailConfirmed = computed(
    () =>
      showFieldConfirmation.value &&
      fieldStates.value?.email !== undefined &&
      fieldStates.value.email !== EnrichmentFieldState.CHECKING,
  );

  function handleResolved(): void {
    if (!isActive.value) return;

    // Resolve chip/buttons now; reveal contact values and checks 250ms later.
    resolveBeatStarted.value = true;
    if (fieldRevealTimeoutId !== null) window.clearTimeout(fieldRevealTimeoutId);
    if (fieldConfirmationTimeoutId !== null) window.clearTimeout(fieldConfirmationTimeoutId);
    const prefersReducedMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const revealDelayMs = prefersReducedMotion ? 0 : 250;

    fieldRevealTimeoutId = window.setTimeout(() => {
      isActive.value = false;
      contactSnapshot.value = null;
      showFieldConfirmation.value = shouldConfirmFields.value;
      fieldRevealTimeoutId = null;

      fieldConfirmationTimeoutId = window.setTimeout(() => {
        showFieldConfirmation.value = false;
        fieldConfirmationTimeoutId = null;
      }, 3500);
    }, revealDelayMs);
  }

  onBeforeUnmount(() => {
    if (fieldRevealTimeoutId !== null) window.clearTimeout(fieldRevealTimeoutId);
    if (fieldConfirmationTimeoutId !== null) window.clearTimeout(fieldConfirmationTimeoutId);
  });

  return {
    displayedEmail,
    displayedPhone,
    handleResolved,
    isActive,
    isButtonPending,
    isEmailConfirmed,
    isPhoneConfirmed,
  };
}
